import type { PresenceEntry, PresenceStatus } from "@mmotdg/shared";
import {
  acceptFriend,
  listFriends as listFriendsApi,
  presenceBatch,
  removeFriendApi,
  sendFriendRequest,
  type FriendEntry,
} from "../../api/AuthClient.js";
import { socialSocket } from "../../api/SocialSocket.js";
import { icon } from "../icons.js";

const SOCIAL_POPOVER_OPEN_EVENT = "social-popover-open";
const POPOVER_ID = "friends";

interface PanelState {
  friends: FriendEntry[];
  presence: Map<string, PresenceEntry>;
  loading: boolean;
  error: string | null;
  open: boolean;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) =>
    c === "&" ? "&amp;" :
    c === "<" ? "&lt;" :
    c === ">" ? "&gt;" :
    c === '"' ? "&quot;" : "&#39;",
  );
}

function statusLabel(s: PresenceStatus): string {
  switch (s) {
    case "online": return "Online";
    case "in_match": return "In match";
    case "in_party": return "In party";
    case "away": return "Away";
    case "offline": return "Offline";
  }
}

export function mountFriendsBell(container: HTMLElement): () => void {
  const root = document.createElement("div");
  root.className = "friends-bell";
  root.innerHTML = `
    <button class="friends-trigger" type="button" aria-label="Friends">
      ${icon("users", 18)}
    </button>
    <div class="friends-dropdown" hidden>
      <div class="friends-dropdown-header">
        <div class="friends-title">${icon("users", 16)} Friends</div>
      </div>
      <form class="friends-add" autocomplete="off">
        <input type="text" name="username" placeholder="Add by username" maxlength="20"/>
        <button type="submit" title="Send friend request">${icon("plus", 14)}</button>
      </form>
      <div class="friends-feedback" hidden></div>
      <div class="friends-section-title" data-section="pending-in" hidden>Incoming requests</div>
      <ul class="friends-list" data-list="pending-in"></ul>
      <div class="friends-section-title" data-section="accepted">Friends</div>
      <ul class="friends-list" data-list="accepted"></ul>
      <div class="friends-empty" data-empty hidden>No friends yet. Add one above.</div>
    </div>
  `;
  container.appendChild(root);

  const state: PanelState = {
    friends: [],
    presence: new Map(),
    loading: true,
    error: null,
    open: false,
  };

  const trigger = root.querySelector<HTMLButtonElement>(".friends-trigger")!;
  const dropdown = root.querySelector<HTMLDivElement>(".friends-dropdown")!;

  function setOpen(open: boolean): void {
    state.open = open;
    dropdown.hidden = !open;
    if (open) {
      document.dispatchEvent(new CustomEvent(SOCIAL_POPOVER_OPEN_EVENT, { detail: POPOVER_ID }));
      void refresh();
    }
  }

  trigger.addEventListener("click", () => setOpen(!state.open));

  const onDocClick = (e: MouseEvent): void => {
    if (!state.open) return;
    if (!root.contains(e.target as Node)) setOpen(false);
  };
  document.addEventListener("click", onDocClick);

  const onPopoverOpen = (e: Event): void => {
    const detail = (e as CustomEvent<string>).detail;
    if (detail !== POPOVER_ID && state.open) setOpen(false);
  };
  document.addEventListener(SOCIAL_POPOVER_OPEN_EVENT, onPopoverOpen);

  const form = root.querySelector<HTMLFormElement>(".friends-add")!;
  const input = form.querySelector<HTMLInputElement>("input[name=username]")!;
  const feedback = root.querySelector<HTMLDivElement>(".friends-feedback")!;
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const username = input.value.trim();
    if (!username) return;
    void addFriend(username);
  });

  async function addFriend(username: string): Promise<void> {
    feedback.hidden = true;
    try {
      await sendFriendRequest(username);
      input.value = "";
      showFeedback(`Request sent to ${username}`, "ok");
      await refresh();
    } catch (err) {
      const code = err instanceof Error ? err.message : "ERROR";
      showFeedback(codeToMessage(code), "err");
    }
  }

  function showFeedback(text: string, kind: "ok" | "err"): void {
    feedback.textContent = text;
    feedback.hidden = false;
    feedback.dataset.kind = kind;
    setTimeout(() => {
      if (feedback.textContent === text) feedback.hidden = true;
    }, 4000);
  }

  function codeToMessage(code: string): string {
    switch (code) {
      case "USER_NOT_FOUND": return "User not found.";
      case "CANNOT_ADD_SELF": return "You cannot add yourself.";
      case "UNAUTHORIZED": return "Session expired.";
      default: return `Error: ${code}`;
    }
  }

  async function refresh(): Promise<void> {
    state.loading = true;
    try {
      const { friends } = await listFriendsApi();
      state.friends = friends;
      const ids = friends.map((f) => f.id);
      if (ids.length > 0) {
        try {
          const { entries } = await presenceBatch(ids);
          state.presence = new Map(entries.map((e) => [e.userId, e]));
        } catch {
          state.presence = new Map();
        }
      } else {
        state.presence = new Map();
      }
      state.error = null;
    } catch (err) {
      state.error = err instanceof Error ? err.message : "ERROR";
    }
    state.loading = false;
    render();
  }

  function render(): void {
    const pendingIn = state.friends.filter((f) => f.status === "pending");
    const accepted = state.friends.filter((f) => f.status === "accepted");
    const all = state.friends.length;

    const empty = root.querySelector<HTMLDivElement>("[data-empty]")!;
    empty.hidden = all > 0 || state.loading;

    renderList(root, "pending-in", pendingIn, (item) => `
      <li class="friend-row pending">
        <div class="friend-info">
          <div class="friend-name">${escapeHtml(item.username)}</div>
          <div class="friend-status">Wants to be friends</div>
        </div>
        <div class="friend-actions">
          <button class="icon-btn ok" data-action="accept" data-id="${item.id}" title="Accept">${icon("check", 14)}</button>
          <button class="icon-btn danger" data-action="decline" data-id="${item.id}" title="Decline">${icon("close", 14)}</button>
        </div>
      </li>
    `);

    renderList(root, "accepted", accepted, (item) => {
      const presence = state.presence.get(item.id);
      const status: PresenceStatus = presence?.status ?? "offline";
      return `
        <li class="friend-row">
          <div class="friend-info">
            <div class="friend-name">
              <span class="presence-dot presence-${status}"></span>
              ${escapeHtml(item.username)}
            </div>
            <div class="friend-status">${statusLabel(status)}</div>
          </div>
          <div class="friend-actions">
            <button class="icon-btn danger" data-action="remove" data-id="${item.id}" title="Remove">${icon("close", 14)}</button>
          </div>
        </li>
      `;
    });
  }

  function renderList(scope: HTMLElement, key: string, items: FriendEntry[], rowHtml: (f: FriendEntry) => string): void {
    const list = scope.querySelector<HTMLUListElement>(`[data-list="${key}"]`)!;
    const title = scope.querySelector<HTMLElement>(`[data-section="${key}"]`);
    list.innerHTML = items.map(rowHtml).join("");
    if (title) title.hidden = items.length === 0 || (key === "accepted" && state.friends.length === 0);
  }

  root.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    const btn = target.closest<HTMLButtonElement>("button[data-action]");
    if (!btn) return;
    const action = btn.dataset.action;
    const id = btn.dataset.id;
    if (!action || !id) return;
    if (action === "accept") void doAccept(id);
    else if (action === "decline" || action === "remove") void doRemove(id);
  });

  async function doAccept(id: string): Promise<void> {
    try {
      await acceptFriend(id);
      await refresh();
    } catch (err) {
      showFeedback("Could not accept request", "err");
    }
  }

  async function doRemove(id: string): Promise<void> {
    try {
      await removeFriendApi(id);
      await refresh();
    } catch {
      showFeedback("Could not remove friend", "err");
    }
  }

  const unsubscribeSocket = socialSocket.onMessage((msg) => {
    if (msg.type === "presence_update") {
      if (msg.entry.status === "offline") {
        state.presence.delete(msg.entry.userId);
      } else {
        state.presence.set(msg.entry.userId, msg.entry);
      }
      render();
    } else if (msg.type === "presence_snapshot") {
      state.presence = new Map(msg.entries.map((e) => [e.userId, e]));
      render();
    } else if (msg.type === "notification_new") {
      if (msg.notification.type === "friend_request" || msg.notification.type === "friend_accepted") {
        void refresh();
      }
    }
  });

  void refresh();

  return () => {
    unsubscribeSocket();
    document.removeEventListener("click", onDocClick);
    document.removeEventListener(SOCIAL_POPOVER_OPEN_EVENT, onPopoverOpen);
    root.remove();
  };
}
